import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { slugify } from '../../utils';
import {
  getAlbum,
  getTracksByAlbum,
  updateAlbum,
  createAlbum,
  createTrack,
  updateTrack,
  deleteTrack,
  uploadCover,
  uploadTrack as uploadTrackFile,
  getCoverPublicUrl,
  type Album,
  type Track,
} from '../../services/api/albums';

const EMPTY_ALBUM: Partial<Album> = {
  title_en: '',
  title_th: '',
  slug: '',
  release_date: null,
  is_single: false,
  is_featured: false,
  sort_order: 0,
};

export default function AlbumEditorPage() {
  const { id } = useParams<{ id: string }>();
  const { pathname, state } = useLocation();
  const navigate = useNavigate();
  const isNew = pathname.endsWith('/music/new');
  const initialSingle = (state as { type?: string })?.type === 'single';
  const [album, setAlbum] = useState<Partial<Album>>({ ...EMPTY_ALBUM, is_single: initialSingle });
  const [tracks, setTracks] = useState<Track[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploadingTrack, setUploadingTrack] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'uploading_cover' | 'saved'>('idle');
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverPreviewUrl, setCoverPreviewUrl] = useState<string | null>(null);
  const [newTrackFile, setNewTrackFile] = useState<File | null>(null);
  const [newTrackTitle, setNewTrackTitle] = useState('');
  const { t } = useTranslation();

  const coverPreviewUrlRef = useRef<string | null>(null);
  // Preview for newly chosen cover (before save); revoke when file changes or unmount
  useEffect(() => {
    if (!coverFile) {
      if (coverPreviewUrlRef.current) {
        URL.revokeObjectURL(coverPreviewUrlRef.current);
        coverPreviewUrlRef.current = null;
      }
      setCoverPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(coverFile);
    coverPreviewUrlRef.current = url;
    setCoverPreviewUrl(url);
    return () => {
      URL.revokeObjectURL(url);
      coverPreviewUrlRef.current = null;
    };
  }, [coverFile]);

  useEffect(() => {
    if (isNew) {
      setLoading(false);
      return;
    }
    if (!id) return;
    setLoading(true);
    Promise.all([getAlbum(id), getTracksByAlbum(id)])
      .then(([a, t]) => {
        setAlbum(a);
        setTracks(t);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id, isNew]);

  function handleAlbumChange(field: keyof Album, value: unknown) {
    setAlbum((prev) => ({
      ...prev,
      [field]: value,
      ...(field === 'title_en' && (!prev.slug || prev.slug === '') ? { slug: slugify(String(value)) } : {}),
    }));
  }

  async function handleSave() {
    setSaving(true);
    setSaveStatus(coverFile ? 'uploading_cover' : 'saving');
    try {
      let albumId = id;
      if (isNew) {
        const slug = album.slug || slugify(album.title_en || 'album');
        albumId = await createAlbum({ ...album, slug });
        navigate(`/admin/music/albums/${albumId}`, { replace: true });
        setAlbum((a) => ({ ...a, id: albumId }));
      } else {
        await updateAlbum(id!, album);
      }
      if (coverFile && albumId && album.slug) {
        setSaveStatus('uploading_cover');
        const path = await uploadCover(album.slug, coverFile);
        await updateAlbum(albumId, { cover_storage_path: path });
        setCoverFile(null);
      }
      if (!isNew) {
        const a = await getAlbum(albumId!);
        setAlbum(a);
      }
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (e) {
      console.error(e);
      setSaveStatus('idle');
    } finally {
      setSaving(false);
    }
  }

  async function handleAddTrack() {
    if (!album.id || !newTrackTitle.trim() || !newTrackFile) return;
    const slug = album.slug || 'album';
    const ext = newTrackFile.name.split('.').pop()?.toLowerCase() || 'mp3';
    const trackSlug = slugify(newTrackTitle) || 'track';
    const path = album.is_single ? `singles/${trackSlug}.${ext}` : `${slug}/${trackSlug}.${ext}`;
    setUploadingTrack(true);
    try {
      await uploadTrackFile(path, newTrackFile);
      await createTrack({
        album_id: album.is_single ? null : album.id,
        title_en: newTrackTitle,
        file_storage_path: path,
        sort_order: tracks.length,
      });
      const updated = await getTracksByAlbum(album.id);
      setTracks(updated);
      setNewTrackTitle('');
      setNewTrackFile(null);
    } catch (e) {
      console.error(e);
    } finally {
      setUploadingTrack(false);
    }
  }

  async function handleUpdateTrack(trackId: string, updates: Partial<Track>) {
    await updateTrack(trackId, updates);
    if (album?.id) setTracks(await getTracksByAlbum(album.id));
  }

  async function handleRemoveTrack(trackId: string) {
    if (!confirm(t('admin.removeTrackConfirm'))) return;
    await deleteTrack(trackId);
    if (album?.id) setTracks(await getTracksByAlbum(album.id));
  }

  if (loading) return <p className="text-[var(--color-text-muted)]">{t('common.loading')}</p>;

  const isSingle = album.is_single ?? false;
  const pageTitle = isNew
    ? (isSingle ? t('admin.newSingle') : t('admin.newAlbum'))
    : (isSingle ? t('admin.editSingle') : t('admin.editAlbum'));
  const saveButtonText = saveStatus === 'uploading_cover'
    ? t('admin.uploadingCover')
    : saveStatus === 'saved'
      ? t('admin.saved')
      : saving
        ? t('admin.savingAlbum')
        : isSingle
          ? t('admin.saveSingle')
          : t('admin.saveAlbum');

  return (
    <div className="admin-page">
      <h1 className="admin-page__title">{pageTitle}</h1>

      <div className="admin-music-type admin-form-group">
        <label className="admin-music-type__label">{t('admin.type')}</label>
        <div className="admin-music-type-options">
          <label className="admin-music-type-option">
            <input
              type="radio"
              name="musicType"
              checked={!isSingle}
              onChange={() => handleAlbumChange('is_single', false)}
            />
            <span>{t('admin.typeAlbum')}</span>
          </label>
          <label className="admin-music-type-option">
            <input
              type="radio"
              name="musicType"
              checked={isSingle}
              onChange={() => handleAlbumChange('is_single', true)}
            />
            <span>{t('admin.typeSingle')}</span>
          </label>
        </div>
      </div>

      <div className="mb-6 grid gap-4 md:grid-cols-2">
        <div className="admin-form-group">
          <label>{t('admin.titleEn')}</label>
          <input
            value={album.title_en ?? ''}
            onChange={(e) => handleAlbumChange('title_en', e.target.value)}
            className="w-full rounded border border-[var(--color-border)] bg-black px-3 py-2 text-[var(--color-text)]"
          />
        </div>
        <div className="admin-form-group">
          <label>{t('admin.titleTh')}</label>
          <input
            value={album.title_th ?? ''}
            onChange={(e) => handleAlbumChange('title_th', e.target.value)}
            className="w-full rounded border border-[var(--color-border)] bg-black px-3 py-2 text-[var(--color-text)]"
          />
        </div>
        <div className="admin-form-group">
          <label>{t('admin.slug')}</label>
          <input
            value={album.slug ?? ''}
            onChange={(e) => handleAlbumChange('slug', e.target.value)}
            className="w-full rounded border border-[var(--color-border)] bg-black px-3 py-2 text-[var(--color-text)]"
          />
        </div>
        <div className="admin-form-group">
          <label className="admin-checkbox-label">
            <input
              type="checkbox"
              checked={album.is_featured ?? false}
              onChange={(e) => handleAlbumChange('is_featured', e.target.checked)}
            />
            {t('admin.showOnHomepage')}
          </label>
        </div>
        <div className="admin-form-group admin-cover-block">
          <label>{t('admin.coverImage')}</label>
          <div className="admin-cover-preview-area">
            {coverPreviewUrl && (
              <div className="admin-cover-preview">
                <span className="admin-cover-preview__label">{t('admin.coverPreview')}</span>
                <img src={coverPreviewUrl} alt="" className="admin-cover-preview__img" />
                <button type="button" onClick={() => setCoverFile(null)} className="admin-btn admin-btn--sm admin-cover-preview__clear">
                  {t('admin.clearCover')}
                </button>
              </div>
            )}
            {album.cover_storage_path && !coverFile && (
              <div className="admin-cover-preview">
                <span className="admin-cover-preview__label">{t('admin.currentCover')}</span>
                <img src={getCoverPublicUrl(album.cover_storage_path)} alt="" className="admin-cover-preview__img" />
              </div>
            )}
            <div className="admin-file-upload">
              <label className="admin-file-upload__label">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setCoverFile(e.target.files?.[0] ?? null)}
                  className="admin-file-upload__input"
                />
                <span className="admin-file-upload__text">{coverFile ? t('admin.changeCover') : t('admin.chooseCover')}</span>
              </label>
              <span className="admin-file-upload__hint">{t('admin.chooseCoverHint')}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="admin-save-row">
        <button onClick={handleSave} disabled={saving} className="admin-btn admin-btn--primary">
          {saveButtonText}
        </button>
        {saveStatus === 'saved' && <span className="admin-save-done">{t('admin.saved')}</span>}
      </div>

      {!isNew && album.id && (
        <>
          <h2 className="admin-section-title">{isSingle ? t('admin.songSection') : t('admin.tracksSection')}</h2>
          <p className="admin-section-hint">{t('admin.addTrackHint')}</p>
          <div className="admin-add-track">
            <div className="admin-form-group">
              <label>{t('admin.trackTitle')}</label>
              <input
                placeholder={t('admin.trackTitle')}
                value={newTrackTitle}
                onChange={(e) => setNewTrackTitle(e.target.value)}
                className="w-full rounded border border-[var(--color-border)] bg-black px-3 py-2 text-[var(--color-text)]"
              />
            </div>
            <div className="admin-form-group">
              <label>{t('admin.chooseMp3')}</label>
              <div className="admin-file-upload">
                <label className="admin-file-upload__label">
                  <input
                    type="file"
                    accept="audio/mpeg,.mp3,audio/*"
                    onChange={(e) => setNewTrackFile(e.target.files?.[0] ?? null)}
                    disabled={uploadingTrack}
                    className="admin-file-upload__input"
                  />
                  <span className="admin-file-upload__text">
                    {newTrackFile ? newTrackFile.name : (uploadingTrack ? t('admin.uploadingTrack') : t('admin.chooseMp3'))}
                  </span>
                </label>
                <span className="admin-file-upload__hint">{t('admin.chooseMp3Hint')}</span>
              </div>
            </div>
            <button
              onClick={handleAddTrack}
              disabled={!newTrackTitle.trim() || !newTrackFile || uploadingTrack}
              className="admin-btn"
            >
              {uploadingTrack ? t('admin.uploadingTrack') : t('admin.addTrack')}
            </button>
          </div>
          <ul className="admin-track-list">
            {tracks.map((tr) => (
              <li key={tr.id} className="admin-track-item">
                <span className="admin-track-item__title">{tr.title_en}</span>
                <div className="admin-track-item__actions">
                  <label className="admin-checkbox-label">
                    <input
                      type="checkbox"
                      checked={tr.is_featured}
                      onChange={(e) => handleUpdateTrack(tr.id, { is_featured: e.target.checked })}
                    />
                    {t('admin.featuredTrack')}
                  </label>
                  <button type="button" onClick={() => handleRemoveTrack(tr.id)} className="admin-btn admin-btn--sm admin-btn--danger">
                    {t('admin.removeTrack')}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
